import { useMemo } from 'react';
import { TableEmpty } from '../components/table/TableEmpty';
import { TableError } from '../components/table/TableError';
import { DEFAULT_LOCALE, translate } from '../utils/translations';

type Props<T> = {
  data?: T[];
  hasError?: boolean;
  isFilter?: boolean;
  isLoading?: boolean;
  emptyUsers?: boolean;
  emptyTenants?: boolean;
  onRetry?(): void;
  locale?: string;
};

export function useEmptyState<T>({
  data,
  hasError,
  isLoading,
  onRetry,
  isFilter,
  emptyTenants,
  emptyUsers,
  locale = DEFAULT_LOCALE,
}: Props<T>) {
  const emptyState = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    if (hasError) {
      return {
        title: translate('Alert!', locale),
        subtitle: translate('Something went wrong.', locale),
        description: translate('Please retry.', locale),
        image: <TableError />,
        button: { text: translate('Retry', locale), onClick: onRetry },
      };
    }
    if (isFilter && !data?.length) {
      return {
        title: translate(
          'Sorry, we don’t find what you are searching.',
          locale,
        ),
        subtitle: translate(
          'Try again changing the search parameters.',
          locale,
        ),
        button: { text: translate('Retry', locale), onClick: onRetry },
        image: <TableEmpty />,
      };
    }
    if (emptyTenants) {
      return {
        title: translate('Add tenants to this user', locale),
        subtitle: translate('Click on top and start adding tenants.', locale),
        image: <TableEmpty />,
      };
    }

    if (emptyUsers) {
      return {
        title: translate('Add users to this tenant', locale),
        subtitle: translate('Click on top and start adding users.', locale),
        image: <TableEmpty />,
      };
    }

    if (!data?.length) {
      return {
        title: translate('There are no data yet.', locale),
        subtitle: '',
        button: { text: translate('Reload page', locale), onClick: onRetry },
        image: <TableEmpty />,
      };
    }
    return undefined;
  }, [data, hasError, isLoading, isFilter, emptyTenants, emptyUsers]);

  return { emptyState };
}
